// Imports
import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components/macro';
import { useAppDispatch } from '../../../../../../shareables/foundation/front-end/redux/hooks';
import apiActions from '../../../../../../shareables/integrations/api/redux/actions';
import { GetFacilitiesResponse } from '../../../../../../shareables/types/api/main/website/page/type/214/facilities/get';


// Styled components
const Wrapper = styled.div<{ hasFacilityPhoto: boolean }>`
	${({ hasFacilityPhoto }) =>
		hasFacilityPhoto &&
		css`
			display: grid;
		`}
	grid-template-columns: 9.375rem 1fr;
	padding-bottom: 2.5rem;
`;

const Photo = styled.img`
	height: auto;
	width: 9.375rem;
	box-shadow: 0 0 0.3125rem rgba(0, 0, 0, 0.2);
`;

const Info = styled.div<{ hasFacilityPhoto: boolean }>`
	${({ hasFacilityPhoto }) =>
		hasFacilityPhoto &&
		css`
			margin-left: 1.25rem;
			grid-column: 2;
		`}
`;

const Title = styled.h3`
	font-size: 1.5rem;
	font-weight: 500;
	line-height: 1.1;
	margin-bottom: 0.625rem;
`;

const Description = styled.p`
	line-height: 1.3125rem;
	white-space: pre-line;
`;


// Define the accepted props
interface Props {
	pageID: number;
	storybookFacilities?: GetFacilitiesResponse;
}


// Function component
const CommunityFacilities: React.FC<Props> = ({ pageID, storybookFacilities }) => {
	// Use state
	const [facilities, setFacilities] = useState<GetFacilitiesResponse | null>(storybookFacilities ?? null);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Load facilities on mount
	useEffect(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetFacilitiesResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/page/id_${pageID}/type/214/facilities`,
				completion: (json) => {
					setFacilities(json.response);
				},
			})
		);
	}, [dispatch, pageID]);
	
	
	// If loading, then display nothing
	if (facilities === null) {
		return null;
	}
	
	
	// See if any facility has an image
	const hasFacilityPhoto = facilities.some((facility) => facility.photo !== undefined);
	
	
	// Return JSX
	return (
		<React.Fragment>
			{facilities.length === 0 && <em>No facilities have been added yet…</em>}
			
			{facilities.map((facility) => {
				return (
					<Wrapper key={facility.id} hasFacilityPhoto={hasFacilityPhoto}>
						{/* Title is shown as visible text right next to the photo, so an alt here would be redundant */}
						{facility.photo && <Photo src={facility.photo} alt='' />}
						
						<Info hasFacilityPhoto={hasFacilityPhoto}>
							<Title>{facility.title}</Title>
							<Description>{facility.description}</Description>
						</Info>
					</Wrapper>
				);
			})}
		</React.Fragment>
	);
};

export default CommunityFacilities;
